import { CSSProperties, ReactNode } from "react";

// Original 5 swatches (legacy — existing users already have these in DB).
// Extended set adds horizontal/vertical stripes, gradient sweeps, polka
// dots, and sine waves so the default-avatar pool reads as "designed
// variety". The Swatch component renders by kind→class lookup; unknown
// kinds fall back to "stripes".
export type SwatchKind =
  | "stripes" | "rust" | "olive" | "ink" | "cream"
  // Horizontal / vertical stripes
  | "horiz-rust" | "horiz-olive" | "vert-sky" | "vert-marigold"
  // Gradient sweeps
  | "gradient-sunset" | "gradient-meadow" | "gradient-twilight" | "gradient-rose"
  // Polka dots
  | "dots-rust" | "dots-olive" | "dots-sky" | "dots-marigold"
  // Sine waves
  | "waves-rust" | "waves-olive" | "waves-sky" | "waves-marigold";

// All swatch kinds in order — handy for hash-picking a deterministic
// default avatar from a user id (see pickDefaultAvatar).
export const SWATCH_KINDS: SwatchKind[] = [
  "stripes", "rust", "olive", "ink", "cream",
  "horiz-rust", "horiz-olive", "vert-sky", "vert-marigold",
  "gradient-sunset", "gradient-meadow", "gradient-twilight", "gradient-rose",
  "dots-rust", "dots-olive", "dots-sky", "dots-marigold",
  "waves-rust", "waves-olive", "waves-sky", "waves-marigold",
];

const KIND_TO_CLASS: Record<SwatchKind, string> = {
  stripes: "swatch-stripes",
  rust: "swatch-rust",
  olive: "swatch-olive",
  ink: "swatch-ink",
  cream: "swatch-cream",
  "horiz-rust": "swatch-horiz-rust",
  "horiz-olive": "swatch-horiz-olive",
  "vert-sky": "swatch-vert-sky",
  "vert-marigold": "swatch-vert-marigold",
  "gradient-sunset": "swatch-gradient-sunset",
  "gradient-meadow": "swatch-gradient-meadow",
  "gradient-twilight": "swatch-gradient-twilight",
  "gradient-rose": "swatch-gradient-rose",
  "dots-rust": "swatch-dots-rust",
  "dots-olive": "swatch-dots-olive",
  "dots-sky": "swatch-dots-sky",
  "dots-marigold": "swatch-dots-marigold",
  "waves-rust": "swatch-waves-rust",
  "waves-olive": "swatch-waves-olive",
  "waves-sky": "swatch-waves-sky",
  "waves-marigold": "swatch-waves-marigold",
};

interface SwatchProps {
  // Accepts either a known swatch keyword or an image URL (http/https/data:).
  kind?: SwatchKind | string;
  label?: string;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

export function Swatch({ kind = "stripes", label, style, className, children }: SwatchProps) {
  const isImageUrl =
    typeof kind === "string" &&
    (kind.startsWith("http://") || kind.startsWith("https://") || kind.startsWith("data:"));

  if (isImageUrl) {
    return (
      <div
        className={className}
        style={{
          position: "relative",
          backgroundImage: `url(${kind})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "hsl(var(--cream))",
          ...style,
        }}
      >
        {children}
      </div>
    );
  }

  const cls = KIND_TO_CLASS[kind as SwatchKind] ?? "swatch-stripes";
  return (
    <div className={`${cls} ${className ?? ""}`} style={{ position: "relative", ...style }}>
      {label && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            letterSpacing: "0.1em",
            color: "rgba(255,255,255,0.7)",
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
      )}
      {children}
    </div>
  );
}
